import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {LeafType, isBenchlingTypeDefList} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {StorableSearchInputWithItems} from 'samples/storables/StorableSearchInput';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';

import {ValueInputProps} from './ValueInputTypes';
import valuesToItemsArray, {itemsToValuesArray} from './valuesToItemsArray';

type StorableLeafType =
  | LeafType.STORABLE
  | LeafType.BOX
  | LeafType.CONTAINER
  | LeafType.LOCATION
  | LeafType.PLATE;

type BaseStorableLinkValueInputProps = ValueInputProps<StorableLeafType> & {
  hideSelectedResults?: boolean;
  storableTypes?: Array<string>;
};

/**
 * @package
 */
function BaseStorableValueInput({
  allowAutoFocus,
  onChange,
  schemaId,
  storableTypes,
  typeDef,
  valueInput,
  disabled,
  hideSelectedResults = false,
}: BaseStorableLinkValueInputProps): JSX.Element {
  const value = valueInput.isValid ? valueInput.value : null;

  return (
    <StorableSearchInputWithItems
      autoFocus={allowAutoFocus}
      shouldAllowMultiple={isBenchlingTypeDefList(typeDef)}
      onChange={(items: Array<Item>): void => {
        const itemIds = itemsToValuesArray(items);

        if (isBenchlingTypeDefList(typeDef)) {
          onChange(itemIds);
        } else {
          onChange(itemIds[0] ?? null);
        }
      }}
      storableTypes={storableTypes ?? undefined}
      storableSchemaId={schemaId}
      selectedItems={valuesToItemsArray(value)}
      hideSelectedResults={hideSelectedResults}
      disabled={disabled}
      isReadOnly={disabled} // TODO(BNCH-57668): keep disabled and isReadOnly separate when behavior is fixed
    />
  );
}

/**
 * @package
 */
export function StorableValueInput(props: ValueInputProps<LeafType.STORABLE>): JSX.Element {
  return <BaseStorableValueInput {...props} />;
}

/**
 * @package
 */
export function BoxValueInput(props: ValueInputProps<LeafType.BOX>): JSX.Element {
  return <BaseStorableValueInput {...props} storableTypes={[ModelTypeConstants.BOX]} />;
}

/**
 * @package
 */
export function ContainerValueInput(props: ValueInputProps<LeafType.CONTAINER>): JSX.Element {
  return <BaseStorableValueInput {...props} storableTypes={[ModelTypeConstants.CONTAINER]} />;
}

/**
 * @package
 */
export function LocationValueInput(props: ValueInputProps<LeafType.LOCATION>): JSX.Element {
  return <BaseStorableValueInput {...props} storableTypes={[ModelTypeConstants.LOCATION]} />;
}

/**
 * @package
 */
export function PlateValueInput(props: ValueInputProps<LeafType.PLATE>): JSX.Element {
  return <BaseStorableValueInput {...props} storableTypes={[ModelTypeConstants.PLATE]} />;
}
